import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, EyeIcon, ClockIcon } from '@heroicons/react/24/outline';

const StreamCard = ({ stream, creator }) => {
  const navigate = useNavigate();

  // Handle missing data gracefully
  const {
    _id,
    title = 'Untitled Stream',
    description = 'No description available',
    thumbnail,
    thumbnailUrl,
    status = 'offline',
    viewers = 0,
    scheduledStartTime
  } = stream || {};

  // Use either creator from props or from stream object
  const streamCreator = creator || stream?.creator || { username: 'Unknown' };

  // Format date for scheduled streams
  const formatScheduledTime = (dateString) => {
    if (!dateString) return 'TBA';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Get appropriate status badge styling
  const getStatusBadge = () => {
    switch (status) {
      case 'live':
        return (
          <span className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center">
            <span className="w-2 h-2 bg-white rounded-full mr-1 animate-pulse"></span>
            LIVE
          </span>
        );
      case 'scheduled':
        return (
          <span className="absolute top-3 left-3 bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center">
            <ClockIcon className="w-3 h-3 mr-1" />
            SCHEDULED
          </span>
        );
      case 'ended':
        return (
          <span className="absolute top-3 left-3 bg-gray-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
            ENDED
          </span>
        );
      default:
        return null;
    }
  };

  // Handle click to navigate to stream details
  const handleClick = () => {
    navigate(`/live/${_id}`);
  };

  return (
    <div
      className="fancy-card group cursor-pointer h-full flex flex-col"
      onClick={handleClick}
    >
      {/* Card Image with Status Badge */}
      <div className="relative overflow-hidden rounded-t-xl h-48">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-dark-800/90 z-10"></div>
        <img
          src={thumbnailUrl || thumbnail || "https://placehold.co/400x200/472069/ffffff?text=BitSport+Live"}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {getStatusBadge()}
      </div>

      {/* Card Content */}
      <div className="p-4 flex flex-col flex-grow">
        <h3 className="text-lg font-semibold text-white mb-2 line-clamp-1">{title}</h3>

        {/* Creator Info */}
        <div className="flex items-center mb-3">
          <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center mr-2">
            {streamCreator.avatar ? (
              <img
                src={streamCreator.avatar}
                alt={streamCreator.username}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <UserIcon className="w-4 h-4 text-white" />
            )}
          </div>
          <span className="text-gray-300 text-sm">{streamCreator.username}</span>
        </div>

        {/* Stream Info */}
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{description}</p>

        <div className="mt-auto flex justify-between items-center">
          {status === 'live' ? (
            <div className="flex items-center text-gray-300 text-sm">
              <EyeIcon className="w-4 h-4 mr-1 text-red-500" />
              {viewers} watching
            </div>
          ) : status === 'scheduled' ? (
            <div className="flex items-center text-gray-300 text-sm">
              <ClockIcon className="w-4 h-4 mr-1 text-blue-500" />
              {formatScheduledTime(scheduledStartTime)}
            </div>
          ) : (
            <div className="text-gray-400 text-sm">Stream ended</div>
          )}

          <button
            className={`px-3 py-1 rounded-lg text-sm font-medium ${
              status === 'live'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : status === 'scheduled'
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-700 text-gray-300'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/live/${_id}`);
            }}
          >
            {status === 'live' ? 'Watch Now' : status === 'scheduled' ? 'Set Reminder' : 'Watch Replay'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StreamCard;
